import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { AuthContext } from 'contexts/AuthContext';
import { networkAdapter } from 'services/NetworkAdapter';

const tabs = ['Hotels', 'Bookings', 'Users'];
const bookingStatuses = ['Confirmed', 'Checked-in', 'Completed', 'Cancelled'];

const emptyHotelForm = {
  title: '',
  city: '',
  subtitle: '',
  price: '',
  ratings: '4',
  benefits: '',
};

const AdminPanel = () => {
  const { isAuthenticated, userDetails, isAuthLoading } =
    useContext(AuthContext);
  const [activeTab, setActiveTab] = useState('Hotels');
  const [hotels, setHotels] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [users, setUsers] = useState([]);
  const [query, setQuery] = useState('');
  const [hotelForm, setHotelForm] = useState(emptyHotelForm);
  const [editingHotelCode, setEditingHotelCode] = useState(null);
  const [isHotelFormVisible, setIsHotelFormVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const loadAdminData = useCallback(async () => {
    setIsLoading(true);
    setErrorMessage('');
    const [hotelResponse, bookingResponse, userResponse] = await Promise.all([
      networkAdapter.get('/api/admin/hotels'),
      networkAdapter.get('/api/admin/bookings'),
      networkAdapter.get('/api/admin/users'),
    ]);
    const failedResponse = [hotelResponse, bookingResponse, userResponse].find(
      (response) => response?.errors?.length
    );
    if (failedResponse) {
      setErrorMessage(failedResponse.errors[0]);
      setIsLoading(false);
      return;
    }

    setHotels(hotelResponse.data.elements);
    setBookings(bookingResponse.data.elements);
    setUsers(userResponse.data.elements);
    setIsLoading(false);
  }, []);

  useEffect(() => {
    if (isAuthenticated && userDetails?.role === 'admin') {
      loadAdminData();
    }
  }, [isAuthenticated, userDetails, loadAdminData]);

  const filteredHotels = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    if (!normalizedQuery) return hotels;
    return hotels.filter((hotel) =>
      [hotel.title, hotel.city, hotel.subtitle]
        .join(' ')
        .toLowerCase()
        .includes(normalizedQuery)
    );
  }, [hotels, query]);

  const filteredBookings = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    if (!normalizedQuery) return bookings;
    return bookings.filter((booking) =>
      [
        booking.bookingId,
        booking.hotelName,
        booking.guestName,
        booking.email,
        booking.status,
      ]
        .join(' ')
        .toLowerCase()
        .includes(normalizedQuery)
    );
  }, [bookings, query]);

  const filteredUsers = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    if (!normalizedQuery) return users;
    return users.filter((user) =>
      [user.fullName, user.email, user.country, user.role]
        .join(' ')
        .toLowerCase()
        .includes(normalizedQuery)
    );
  }, [users, query]);

  const clearMessages = () => {
    setErrorMessage('');
    setSuccessMessage('');
  };

  const startAddHotel = () => {
    clearMessages();
    setHotelForm(emptyHotelForm);
    setEditingHotelCode(null);
    setIsHotelFormVisible(true);
  };

  const startEditHotel = (hotel) => {
    clearMessages();
    setHotelForm({
      title: hotel.title,
      city: hotel.city,
      subtitle: hotel.subtitle,
      price: hotel.price,
      ratings: hotel.ratings,
      benefits: hotel.benefits.join(', '),
    });
    setEditingHotelCode(hotel.hotelCode);
    setIsHotelFormVisible(true);
  };

  const saveHotel = async (event) => {
    event.preventDefault();
    clearMessages();
    const payload = {
      ...hotelForm,
      benefits: hotelForm.benefits
        .split(',')
        .map((benefit) => benefit.trim())
        .filter(Boolean),
    };
    const response = editingHotelCode
      ? await networkAdapter.patch(
          `/api/admin/hotels/${editingHotelCode}`,
          payload
        )
      : await networkAdapter.post('/api/admin/hotels', payload);

    if (response?.errors?.length) {
      setErrorMessage(response.errors[0]);
      return;
    }
    setSuccessMessage(
      editingHotelCode
        ? 'Hotel updated successfully.'
        : 'Hotel added successfully.'
    );
    setIsHotelFormVisible(false);
    setEditingHotelCode(null);
    await loadAdminData();
  };

  const deleteHotel = async (hotel) => {
    if (!window.confirm(`Delete ${hotel.title}?`)) return;
    clearMessages();
    const response = await networkAdapter.delete(
      `/api/admin/hotels/${hotel.hotelCode}`
    );
    if (response?.errors?.length) {
      setErrorMessage(response.errors[0]);
      return;
    }
    setSuccessMessage('Hotel deleted successfully.');
    await loadAdminData();
  };

  const updateBookingStatus = async (bookingId, status) => {
    clearMessages();
    const response = await networkAdapter.patch(
      `/api/admin/bookings/${bookingId}`,
      { status }
    );
    if (response?.errors?.length) {
      setErrorMessage(response.errors[0]);
      return;
    }
    setBookings((current) =>
      current.map((booking) =>
        booking.bookingId === bookingId ? { ...booking, status } : booking
      )
    );
    setSuccessMessage('Booking status updated.');
  };

  const updateUserStatus = async (user) => {
    clearMessages();
    const response = await networkAdapter.patch(`/api/admin/users/${user.id}`, {
      isActive: !user.isActive,
    });
    if (response?.errors?.length) {
      setErrorMessage(response.errors[0]);
      return;
    }
    setUsers((current) =>
      current.map((item) =>
        item.id === user.id ? { ...item, isActive: !user.isActive } : item
      )
    );
    setSuccessMessage(
      user.isActive ? 'User account deactivated.' : 'User account activated.'
    );
  };

  if (isAuthLoading) {
    return <div className="container mx-auto p-8">Checking admin access…</div>;
  }
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  if (userDetails?.role !== 'admin') {
    return <Navigate to="/user-profile" replace />;
  }

  const counts = {
    Hotels: hotels.length,
    Bookings: bookings.length,
    Users: users.length,
  };

  return (
    <main className="container mx-auto min-h-[600px] px-4 py-8">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
        <div>
          <p className="text-sm font-semibold uppercase tracking-wide text-brand">
            Novastay management
          </p>
          <h1 className="text-3xl font-bold text-slate-800">Admin panel</h1>
        </div>
        <span className="rounded-full bg-blue-50 px-4 py-2 text-sm text-brand">
          Signed in as {userDetails.fullName}
        </span>
      </div>

      {(errorMessage || successMessage) && (
        <div
          role={errorMessage ? 'alert' : 'status'}
          className={`mb-4 rounded border p-3 ${
            errorMessage
              ? 'border-red-200 bg-red-50 text-red-800'
              : 'border-green-200 bg-green-50 text-green-800'
          }`}
        >
          {errorMessage || successMessage}
        </div>
      )}

      <div className="mb-6 grid grid-cols-1 gap-3 sm:grid-cols-3">
        {tabs.map((tab) => (
          <button
            key={tab}
            type="button"
            onClick={() => {
              setActiveTab(tab);
              setQuery('');
              clearMessages();
            }}
            className={`rounded-lg border p-4 text-left ${
              activeTab === tab
                ? 'border-brand bg-brand text-white'
                : 'border-slate-200 bg-white text-slate-700'
            }`}
          >
            <span className="block text-sm">{tab}</span>
            <span className="block text-2xl font-bold">{counts[tab]}</span>
          </button>
        ))}
      </div>

      <section className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <h2 className="text-xl font-semibold text-slate-800">
            {activeTab} management
          </h2>
          <div className="flex flex-wrap gap-2">
            <input
              type="search"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder={`Search ${activeTab.toLowerCase()}…`}
              aria-label={`Search ${activeTab.toLowerCase()}`}
              className="rounded border border-slate-300 px-3 py-2"
            />
            {activeTab === 'Hotels' && (
              <button
                type="button"
                onClick={startAddHotel}
                className="rounded bg-brand px-4 py-2 font-medium text-white hover:bg-blue-900"
              >
                Add hotel
              </button>
            )}
          </div>
        </div>

        {activeTab === 'Hotels' && isHotelFormVisible && (
          <form
            onSubmit={saveHotel}
            className="mb-5 grid grid-cols-1 gap-3 rounded border border-slate-200 bg-slate-50 p-4 md:grid-cols-2"
          >
            <h3 className="md:col-span-2 text-lg font-semibold">
              {editingHotelCode ? 'Edit hotel' : 'Add hotel'}
            </h3>
            <label className="flex flex-col gap-1 text-sm">
              Hotel name
              <input
                required
                value={hotelForm.title}
                onChange={(event) =>
                  setHotelForm({ ...hotelForm, title: event.target.value })
                }
                className="rounded border border-slate-300 px-3 py-2"
              />
            </label>
            <label className="flex flex-col gap-1 text-sm">
              City
              <input
                required
                value={hotelForm.city}
                onChange={(event) =>
                  setHotelForm({ ...hotelForm, city: event.target.value })
                }
                className="rounded border border-slate-300 px-3 py-2"
              />
            </label>
            <label className="flex flex-col gap-1 text-sm md:col-span-2">
              U.S. address
              <input
                required
                value={hotelForm.subtitle}
                onChange={(event) =>
                  setHotelForm({ ...hotelForm, subtitle: event.target.value })
                }
                className="rounded border border-slate-300 px-3 py-2"
              />
            </label>
            <label className="flex flex-col gap-1 text-sm">
              Nightly price (USD)
              <input
                required
                min="1"
                step="0.01"
                type="number"
                value={hotelForm.price}
                onChange={(event) =>
                  setHotelForm({ ...hotelForm, price: event.target.value })
                }
                className="rounded border border-slate-300 px-3 py-2"
              />
            </label>
            <label className="flex flex-col gap-1 text-sm">
              Rating (1–5)
              <input
                required
                min="1"
                max="5"
                step="0.1"
                type="number"
                value={hotelForm.ratings}
                onChange={(event) =>
                  setHotelForm({ ...hotelForm, ratings: event.target.value })
                }
                className="rounded border border-slate-300 px-3 py-2"
              />
            </label>
            <label className="flex flex-col gap-1 text-sm md:col-span-2">
              Benefits (comma separated)
              <input
                value={hotelForm.benefits}
                onChange={(event) =>
                  setHotelForm({ ...hotelForm, benefits: event.target.value })
                }
                className="rounded border border-slate-300 px-3 py-2"
              />
            </label>
            <div className="flex gap-2 md:col-span-2">
              <button
                type="submit"
                className="rounded bg-brand px-4 py-2 text-white"
              >
                Save hotel
              </button>
              <button
                type="button"
                onClick={() => setIsHotelFormVisible(false)}
                className="rounded border border-slate-300 px-4 py-2"
              >
                Cancel
              </button>
            </div>
          </form>
        )}

        {isLoading ? (
          <p className="py-8 text-center text-slate-600">Loading admin data…</p>
        ) : (
          <div className="overflow-x-auto">
            {activeTab === 'Hotels' && (
              <table className="w-full min-w-[760px] text-left text-sm">
                <thead className="border-b bg-slate-50 text-slate-600">
                  <tr>
                    <th className="p-3">Hotel</th>
                    <th className="p-3">City</th>
                    <th className="p-3">Nightly rate</th>
                    <th className="p-3">Rating</th>
                    <th className="p-3">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredHotels.map((hotel) => (
                    <tr key={hotel.hotelCode} className="border-b">
                      <td className="p-3">
                        <p className="font-semibold">{hotel.title}</p>
                        <p className="max-w-md text-xs text-slate-500">
                          {hotel.subtitle}
                        </p>
                      </td>
                      <td className="p-3 capitalize">{hotel.city}</td>
                      <td className="p-3">
                        ${Number(hotel.price).toLocaleString('en-US')} USD
                      </td>
                      <td className="p-3">{hotel.ratings} / 5</td>
                      <td className="p-3">
                        <div className="flex gap-2">
                          <button
                            type="button"
                            onClick={() => startEditHotel(hotel)}
                            className="font-medium text-brand hover:underline"
                          >
                            Edit
                          </button>
                          <button
                            type="button"
                            onClick={() => deleteHotel(hotel)}
                            className="font-medium text-red-700 hover:underline"
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            {activeTab === 'Bookings' && (
              <table className="w-full min-w-[760px] text-left text-sm">
                <thead className="border-b bg-slate-50 text-slate-600">
                  <tr>
                    <th className="p-3">Booking</th>
                    <th className="p-3">Guest</th>
                    <th className="p-3">Hotel / dates</th>
                    <th className="p-3">Total</th>
                    <th className="p-3">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredBookings.map((booking) => (
                    <tr key={booking.bookingId} className="border-b">
                      <td className="p-3">
                        <p className="font-semibold">{booking.bookingId}</p>
                        <p className="text-xs text-slate-500">
                          Booked {booking.bookingDate}
                        </p>
                      </td>
                      <td className="p-3">
                        <p>{booking.guestName}</p>
                        <p className="text-xs text-slate-500">
                          {booking.email}
                        </p>
                      </td>
                      <td className="p-3">
                        <p>{booking.hotelName}</p>
                        <p className="text-xs text-slate-500">
                          {booking.checkInDate} – {booking.checkOutDate}
                        </p>
                      </td>
                      <td className="p-3">{booking.totalFare}</td>
                      <td className="p-3">
                        <select
                          aria-label={`Status for ${booking.bookingId}`}
                          value={booking.status}
                          onChange={(event) =>
                            updateBookingStatus(
                              booking.bookingId,
                              event.target.value
                            )
                          }
                          className="rounded border border-slate-300 px-2 py-1"
                        >
                          {bookingStatuses.map((status) => (
                            <option key={status} value={status}>
                              {status}
                            </option>
                          ))}
                        </select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            {activeTab === 'Users' && (
              <table className="w-full min-w-[600px] text-left text-sm">
                <thead className="border-b bg-slate-50 text-slate-600">
                  <tr>
                    <th className="p-3">User</th>
                    <th className="p-3">Country</th>
                    <th className="p-3">Role</th>
                    <th className="p-3">Account</th>
                    <th className="p-3">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.map((user) => (
                    <tr key={user.id} className="border-b">
                      <td className="p-3">
                        <p className="font-semibold">{user.fullName}</p>
                        <p className="text-xs text-slate-500">{user.email}</p>
                      </td>
                      <td className="p-3">{user.country || '—'}</td>
                      <td className="p-3 capitalize">{user.role}</td>
                      <td className="p-3">
                        {user.isActive ? 'Active' : 'Inactive'}
                      </td>
                      <td className="p-3">
                        {user.role === 'admin' ? (
                          <span className="text-slate-500">Protected</span>
                        ) : (
                          <button
                            type="button"
                            onClick={() => updateUserStatus(user)}
                            className="font-medium text-brand hover:underline"
                          >
                            {user.isActive ? 'Deactivate' : 'Activate'}
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
        {!isLoading &&
          ((activeTab === 'Hotels' && filteredHotels.length === 0) ||
            (activeTab === 'Bookings' && filteredBookings.length === 0) ||
            (activeTab === 'Users' && filteredUsers.length === 0)) && (
            <p className="py-8 text-center text-slate-500">
              No matching {activeTab.toLowerCase()} found.
            </p>
          )}
      </section>
    </main>
  );
};

export default AdminPanel;
